/**
 * Pure date-arithmetic and relative-input parsing for <DatePicker>. Extracted
 * from date_picker.tsx so these can be unit/PBT-tested directly without
 * mounting the component.
 */

export const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A relative day offset beyond this is rejected as a typo rather than
 * resolved. There's no legitimate due-date/expiry use case anywhere near 100
 * years out, and staying well inside the ECMAScript `Date` range (whose
 * actual limit is ~100,000,000 days from the epoch) means `add_days_to_iso`
 * never has to produce an Invalid Date.
 */
export const MAX_RELATIVE_DAYS = 36_500; // 100 years

/**
 * True only for a `YYYY-MM-DD` string that is a REAL calendar date, not just
 * one matching the shape — rejects e.g. "2026-02-30" or "2026-13-01". Guards
 * the relative-input base date: without this, an invalid-but-shape-matching
 * base would silently normalize (via `Date.UTC`'s own overflow behavior)
 * instead of being treated as "no valid base to compute from yet".
 */
export function is_valid_iso_date(iso: string): boolean {
  if (!ISO_DATE_RE.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
}

/**
 * Adds `days` to an ISO `YYYY-MM-DD` date. UTC-safe: parses/writes via
 * `Date.UTC`/`setUTCDate` so DST and local-timezone offsets never shift the
 * result — correct across month/year boundaries and leap years. Callers must
 * validate `iso` and `days` (see `is_valid_iso_date`/`MAX_RELATIVE_DAYS`)
 * first — this function does not re-validate and will produce nonsense
 * ("NaN-NaN-NaN") for a wildly out-of-range `days`.
 */
export function add_days_to_iso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  return `${String(date.getUTCFullYear())}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Parses a strict `+<digits>` relative offset (e.g. "+30") relative to
 * `relative_base`. Returns null — treat as ordinary unparseable input, same
 * as any other typo: no `onChange`, silent revert to the last good value —
 * when:
 *  - the text isn't that exact pattern (rejects "+abc", "++30", "30+", "+-5")
 *  - the offset is not a positive integer (rejects "+0" — only a positive
 *    relative value is valid)
 *  - the offset exceeds `MAX_RELATIVE_DAYS` (rejects e.g. "+99999999", which
 *    would otherwise overflow `add_days_to_iso` to "NaN-NaN-NaN")
 *  - there's no valid, REAL calendar-date base to resolve it against yet
 *    (e.g. the form's Issue Date hasn't been picked, or is malformed)
 */
export function parse_relative_input(
  raw: string,
  relative_base: string | null | undefined,
): { days: number; iso: string } | null {
  const match = /^\+(\d+)$/.exec(raw.trim());
  if (!match) return null;
  const days = parseInt(match[1], 10);
  if (days < 1 || days > MAX_RELATIVE_DAYS) return null;
  if (!relative_base || !is_valid_iso_date(relative_base)) return null;
  return { days, iso: add_days_to_iso(relative_base, days) };
}

/** How many years either side of the viewed year the Year dropdown offers. */
export const YEAR_SPAN = 10;

/**
 * Years offered by the calendar's Year dropdown: a window of `YEAR_SPAN`
 * years either side of the viewed year, clamped to the `min` / `max` bounds
 * (ISO `YYYY-MM-DD`; empty or absent means unbounded).
 *
 * When the viewed year itself lies outside the bounds, the window is centred
 * on the nearest bound instead, so the reachable years stay selectable, and
 * `view_year` is still included so the select always has an option matching
 * its value.
 */
export function build_year_options(
  view_year: number,
  min?: string,
  max?: string,
): number[] {
  const min_year = min ? Number(min.slice(0, 4)) : Number.NaN;
  const max_year = max ? Number(max.slice(0, 4)) : Number.NaN;
  const lo = Number.isFinite(min_year) ? min_year : -Infinity;
  const hi = Number.isFinite(max_year) ? max_year : Infinity;
  const centre = Math.min(Math.max(view_year, lo), hi);
  const start = Math.min(Math.max(centre - YEAR_SPAN, lo), view_year);
  const end = Math.max(Math.min(centre + YEAR_SPAN, hi), view_year);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}
